import { currentLocale } from '../i18n'
import type { Analysis, FinalComment, FreeComment, Metrics } from './types'

/**
 * Локальная эвристика — когда сервер/ИИ недоступен. Продукт не должен
 * ломаться из-за сети: рисунок, метрики и журнал важнее отклика.
 */

export function localAnalysis(m: Metrics | null, mood: number | null): Analysis {
  const ru = currentLocale() === 'ru'
  if (!m) {
    return ru
      ? {
          analysis: `Рисунок с бумаги — вижу характер линий и палитру, но не движение руки. С оценкой ${mood}/9 проверим это в практике: там линия под полным наблюдением.`,
          questions: ['Что ты чувствовал, когда рисовал это?', 'Какое место рисунка притягивает взгляд?'],
          focus: 'Скруглить самые резкие места — медленно, на выдохе.',
        }
      : {
          analysis: `A paper drawing — I can see the character of the lines and the palette, but not the movement of the hand. With your ${mood}/9 we'll check it in the practice: there the line is fully observed.`,
          questions: ['What were you feeling while you drew this?', 'Which part of the drawing pulls your eye?'],
          focus: 'Round the sharpest places — slowly, on the exhale.',
        }
  }
  const tense = m.sharpPct > 45 || m.speedCV > 0.9
  if (ru) {
    return {
      analysis: tense
        ? `Много резких смен направления (${m.sharpPct}%) и рваный темп. Вместе с оценкой ${mood}/9 похоже на накопленное напряжение без выхода.`
        : `Линии плавные (${m.sharpPct}% острых), темп ровный. С оценкой ${mood}/9 — спокойное состояние, работаем мягко.`,
      questions: ['Какая часть появилась первой — о чём она?', 'Если бы рисунок говорил, что бы попросил?'],
      focus: tense ? 'Скруглить резкие углы — медленно, на выдохе.' : 'Собрать элементы в одно целое.',
    }
  }
  return {
    analysis: tense
      ? `Many sharp changes of direction (${m.sharpPct}%) and a ragged tempo. Together with your ${mood}/9 it reads like tension with nowhere to go.`
      : `The lines are smooth (${m.sharpPct}% sharp), the tempo is even. With your ${mood}/9 — a calm state, we'll work gently.`,
    questions: ['Which part came first — what is it about?', 'If the drawing could speak, what would it ask for?'],
    focus: tense ? 'Round the sharp corners — slowly, on the exhale.' : 'Gather the elements into one whole.',
  }
}

export function localFreeComment(m: Metrics | null): FreeComment {
  const ru = currentLocale() === 'ru'
  const hot = !!m && m.sharpPct > 50
  if (ru) {
    return {
      text: hot
        ? 'Энергичный рисунок — много резких, быстрых линий, рука явно хотела выговориться. В нём есть напор.'
        : 'Свободные, спокойные линии — рисунок дышит. Видно, что рука шла без спешки.',
      state: hot ? 'энергия на выходе' : 'спокойный поток',
    }
  }
  return {
    text: hot
      ? 'An energetic drawing — lots of sharp, fast lines; the hand clearly wanted to speak out. There is drive in it.'
      : 'Free, calm lines — the drawing breathes. You can see the hand moved without hurry.',
    state: hot ? 'energy on its way out' : 'a calm flow',
  }
}

export function localFinal(mb: Metrics | null, me: Metrics | null, calmArcs: number): FinalComment {
  const ru = currentLocale() === 'ru'
  const after = me ?? { sharpPct: 0, speedCV: 0 }
  if (!mb) {
    return ru
      ? {
          text: `${calmArcs} дуг и мазков легли в ритм, к финалу рука шла ровнее. Завтра нарисуй «до» в приложении — увидим и стартовую моторику.`,
          state: 'собранное спокойствие',
        }
      : {
          text: `${calmArcs} arcs and strokes landed in rhythm; by the end the hand ran smoother. Tomorrow draw the "before" in the app — we'll see your starting motor data too.`,
          state: 'collected calm',
        }
  }
  const calmer = after.sharpPct < mb.sharpPct
  if (ru) {
    return {
      text:
        (calmer
          ? `Рука успокоилась: острых поворотов ${after.sharpPct}% против ${mb.sharpPct}%. Цвет собрал рисунок в целое.`
          : 'Рука не сразу поддалась ритму — телу нужно больше времени. Цвет уже смягчил рисунок.') +
        ' Завтра начни сразу с медленной дуги.',
      state: calmer ? 'выровненное спокойствие' : 'напряжение на спаде',
    }
  }
  return {
    text:
      (calmer
        ? `The hand settled: sharp turns ${after.sharpPct}% against ${mb.sharpPct}%. The color pulled the drawing together.`
        : 'The hand did not give in to the rhythm at once — the body needs more time. The color already softened the drawing.') +
      ' Tomorrow start straight with a slow arc.',
    state: calmer ? 'levelled calm' : 'tension on the way down',
  }
}
